"use client";

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { MatchResult, ScoreCounter, ScoreInput } from "../base-match-template";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const parseIntOrZero = (value: string) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const parseOptionalInt = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  if (value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export type FootballScoreEditorProps = {
  initialData?: Record<string, unknown> | null;
  onChange: (data: Record<string, unknown>) => void;
};

export function FootballScoreEditor({
  initialData,
  onChange,
}: FootballScoreEditorProps) {
  const initialMatchScore = isRecord(initialData?.matchScore)
    ? (initialData?.matchScore as Record<string, unknown>)
    : null;

  const [yourGoals, setYourGoals] = useState(
    parseOptionalInt(initialMatchScore?.yourGoals) ?? 0,
  );
  const [opponentGoals, setOpponentGoals] = useState(
    parseOptionalInt(initialMatchScore?.opponentGoals) ?? 0,
  );

  const initialPeriods = Array.isArray(initialData?.periods)
    ? (initialData?.periods as unknown[])
    : [];

  const initialPenalties = isRecord(initialData?.penalties)
    ? (initialData?.penalties as Record<string, unknown>)
    : null;

  const [showDetails, setShowDetails] = useState(
    initialPeriods.length > 0 || initialPenalties != null,
  );

  const findPeriod = (label: string, key: "yourScore" | "opponentScore") => {
    const row = initialPeriods.find(
      (p) => isRecord(p) && typeof p.label === "string" && p.label === label,
    ) as Record<string, unknown> | undefined;
    return String(parseOptionalInt(row?.[key]) ?? "");
  };

  const [firstHalf, setFirstHalf] = useState(() => ({
    your: findPeriod("1H", "yourScore"),
    opp: findPeriod("1H", "opponentScore"),
  }));
  const [secondHalf, setSecondHalf] = useState(() => ({
    your: findPeriod("2H", "yourScore"),
    opp: findPeriod("2H", "opponentScore"),
  }));
  const [includeExtraTime, setIncludeExtraTime] = useState(
    initialPeriods.some(
      (p) => isRecord(p) && typeof p.label === "string" && p.label === "ET",
    ),
  );
  const [extraTime, setExtraTime] = useState(() => ({
    your: findPeriod("ET", "yourScore"),
    opp: findPeriod("ET", "opponentScore"),
  }));

  const [includePenalties, setIncludePenalties] = useState(
    Boolean(initialPenalties),
  );
  const [penalties, setPenalties] = useState(() => ({
    your: String(parseOptionalInt(initialPenalties?.yourScore) ?? ""),
    opp: String(parseOptionalInt(initialPenalties?.opponentScore) ?? ""),
  }));

  const derivedTotals = useMemo(() => {
    if (!showDetails) return null;
    const h1y = parseIntOrZero(firstHalf.your);
    const h1o = parseIntOrZero(firstHalf.opp);
    const h2y = parseIntOrZero(secondHalf.your);
    const h2o = parseIntOrZero(secondHalf.opp);
    const ety = includeExtraTime ? parseIntOrZero(extraTime.your) : 0;
    const eto = includeExtraTime ? parseIntOrZero(extraTime.opp) : 0;
    return { your: h1y + h2y + ety, opp: h1o + h2o + eto };
  }, [
    showDetails,
    firstHalf.your,
    firstHalf.opp,
    secondHalf.your,
    secondHalf.opp,
    includeExtraTime,
    extraTime.your,
    extraTime.opp,
  ]);

  useEffect(() => {
    if (!showDetails) return;
    if (!derivedTotals) return;
    setYourGoals(derivedTotals.your);
    setOpponentGoals(derivedTotals.opp);
  }, [showDetails, derivedTotals]);

  useEffect(() => {
    const periods = showDetails
      ? ([
          {
            label: "1H",
            yourScore: parseIntOrZero(firstHalf.your),
            opponentScore: parseIntOrZero(firstHalf.opp),
          },
          {
            label: "2H",
            yourScore: parseIntOrZero(secondHalf.your),
            opponentScore: parseIntOrZero(secondHalf.opp),
          },
          ...(includeExtraTime
            ? [
                {
                  label: "ET",
                  yourScore: parseIntOrZero(extraTime.your),
                  opponentScore: parseIntOrZero(extraTime.opp),
                },
              ]
            : []),
        ] satisfies Array<{
          label: string;
          yourScore: number;
          opponentScore: number;
        }>)
      : [];

    const penaltiesData =
      showDetails && includePenalties
        ? {
            yourScore: parseIntOrZero(penalties.your),
            opponentScore: parseIntOrZero(penalties.opp),
          }
        : null;

    onChange({
      matchScore: { yourGoals, opponentGoals },
      periods,
      penalties: penaltiesData,
    });
  }, [
    yourGoals,
    opponentGoals,
    showDetails,
    firstHalf.your,
    firstHalf.opp,
    secondHalf.your,
    secondHalf.opp,
    includeExtraTime,
    extraTime.your,
    extraTime.opp,
    includePenalties,
    penalties.your,
    penalties.opp,
    onChange,
  ]);

  const yourWon = yourGoals > opponentGoals;
  const opponentWon = opponentGoals > yourGoals;

  return (
    <div className="space-y-3">
      <Label className="text-text-primary">Final Score</Label>
      <div className="flex items-center justify-center gap-4">
        <ScoreCounter
          label="Team 1"
          value={yourGoals}
          onChange={setYourGoals}
          max={20}
          isWinner={yourWon}
          variant="brand"
          disabled={showDetails}
        />
        <span className="text-2xl font-bold text-text-secondary">–</span>
        <ScoreCounter
          label="Team 2"
          value={opponentGoals}
          onChange={setOpponentGoals}
          max={20}
          isWinner={opponentWon}
          variant="opponent"
          disabled={showDetails}
        />
      </div>
      <MatchResult yourScore={yourGoals} opponentScore={opponentGoals} />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-text-primary">Breakdown (Optional)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? "Totals only" : "Add details"}
          </Button>
        </div>

        {showDetails && (
          <div className="space-y-4 rounded-3xl bg-surface-alt/40 p-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
                  1H
                </span>
                <div className="flex items-center gap-3">
                  <ScoreInput
                    value={firstHalf.your}
                    onChange={(e) =>
                      setFirstHalf((prev) => ({
                        ...prev,
                        your: e.target.value,
                      }))
                    }
                    placeholder="-"
                    min={0}
                    max={20}
                  />
                  <span className="font-medium text-text-tertiary">–</span>
                  <ScoreInput
                    value={firstHalf.opp}
                    onChange={(e) =>
                      setFirstHalf((prev) => ({ ...prev, opp: e.target.value }))
                    }
                    placeholder="-"
                    min={0}
                    max={20}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
                  2H
                </span>
                <div className="flex items-center gap-3">
                  <ScoreInput
                    value={secondHalf.your}
                    onChange={(e) =>
                      setSecondHalf((prev) => ({
                        ...prev,
                        your: e.target.value,
                      }))
                    }
                    placeholder="-"
                    min={0}
                    max={20}
                  />
                  <span className="font-medium text-text-tertiary">–</span>
                  <ScoreInput
                    value={secondHalf.opp}
                    onChange={(e) =>
                      setSecondHalf((prev) => ({
                        ...prev,
                        opp: e.target.value,
                      }))
                    }
                    placeholder="-"
                    min={0}
                    max={20}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant={includeExtraTime ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIncludeExtraTime((prev) => !prev)}
                  className={cn(
                    includeExtraTime && "bg-brand text-text-inverse",
                  )}
                >
                  Extra Time
                </Button>
                <Button
                  type="button"
                  variant={includePenalties ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIncludePenalties((prev) => !prev)}
                  className={cn(
                    includePenalties && "bg-brand text-text-inverse",
                  )}
                >
                  Penalties
                </Button>
              </div>

              {includeExtraTime && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
                    ET
                  </span>
                  <div className="flex items-center gap-3">
                    <ScoreInput
                      value={extraTime.your}
                      onChange={(e) =>
                        setExtraTime((prev) => ({
                          ...prev,
                          your: e.target.value,
                        }))
                      }
                      placeholder="-"
                      min={0}
                      max={20}
                    />
                    <span className="font-medium text-text-tertiary">–</span>
                    <ScoreInput
                      value={extraTime.opp}
                      onChange={(e) =>
                        setExtraTime((prev) => ({
                          ...prev,
                          opp: e.target.value,
                        }))
                      }
                      placeholder="-"
                      min={0}
                      max={20}
                    />
                  </div>
                </div>
              )}

              {includePenalties && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
                    Pens
                  </span>
                  <div className="flex items-center gap-3">
                    <ScoreInput
                      value={penalties.your}
                      onChange={(e) =>
                        setPenalties((prev) => ({
                          ...prev,
                          your: e.target.value,
                        }))
                      }
                      placeholder="-"
                      min={0}
                      max={20}
                    />
                    <span className="font-medium text-text-tertiary">–</span>
                    <ScoreInput
                      value={penalties.opp}
                      onChange={(e) =>
                        setPenalties((prev) => ({
                          ...prev,
                          opp: e.target.value,
                        }))
                      }
                      placeholder="-"
                      min={0}
                      max={20}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
